import { useCallback, useEffect, useState } from "react";
import { api } from "./api.ts";

export interface AccountUsage {
  organizationName: string; scope: "organization"; canViewCosts: boolean;
  monthStart: string; resetAt: string; asOf: string;
  limitCny: number; spentCny: number; remainingCny: number; usedPercent: number;
  status: "normal" | "warning" | "exhausted";
}
export interface UsageDetails {
  period: "month" | "7d" | "30d"; since: string; asOf: string; totalCny: number; totalCalls: number;
  byDay: { day: string; calls: number; costCny: number }[];
  byPurpose: { purpose: string; calls: number; costCny: number }[];
}
export interface AccountUsageState { data?: AccountUsage; loading: boolean; error: string; refresh: () => void }

/** Refresh on return and while visible; never reuse the previous signed-in account's snapshot. */
export function useAccountUsage(identity?: string): AccountUsageState {
  const [state, setState] = useState<{ identity: string; data?: AccountUsage; loading: boolean; error: string }>();
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (!identity) return;
    let active = true, inFlight = false;
    const load = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      setState((old) => ({ identity, data: old?.identity === identity ? old.data : undefined, loading: true, error: "" }));
      try {
        const result = await api.get<{ usage: AccountUsage }>("/api/account/usage");
        if (active) setState({ identity, data: result.usage, loading: false, error: "" });
      } catch (e) {
        if (active) setState((old) => ({ identity, data: old?.identity === identity ? old.data : undefined, loading: false, error: (e as Error).message }));
      } finally { inFlight = false; }
    };
    const visible = (): void => { if (document.visibilityState === "visible") void load(); };
    void load();
    const timer = window.setInterval(visible, 60_000);
    window.addEventListener("focus", visible);
    document.addEventListener("visibilitychange", visible);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", visible); document.removeEventListener("visibilitychange", visible); };
  }, [identity, revision]);
  const current = identity && state?.identity === identity ? state : undefined;
  return { data: current?.data, loading: current?.loading ?? Boolean(identity), error: current?.error ?? "", refresh };
}

export const usageMoney = (value: number): string => value > 0 && value < .01 ? "<¥0.01" : new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
export const exactUsageMoney = (value: number): string => "¥" + value.toLocaleString("zh-CN", { maximumFractionDigits: 6 });
export const usageDate = (value: string, time = false): string => new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "numeric", day: "numeric", ...(time ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}) }).format(new Date(value));
export const usagePercent = (value: number): string => value > 0 && value < .1 ? "<0.1%" : `${Math.floor(value * 10) / 10}%`;
